import { isNil } from "lodash";

/**
 * The Relay cursor arguments that fetch one page of logs. Exactly one of
 * `first` / `last` is set, matching the backend's connection-argument
 * validation (it rejects both together).
 */
export type PageCursorArgs = {
  readonly first?: number;
  readonly last?: number;
  readonly after?: string;
  readonly before?: string;
};

/**
 * Translate a paginator page click into cursor arguments.
 *
 * Cursor pagination can only express four of the moves a paginator offers:
 * the head of the list, one page forward, one page back, and the tail. Each is
 * exact — a forward step reads strictly after the current page's last row, so
 * stepping onto the final page shows only the rows that remain rather than the
 * overlapping "last `pageSize` rows" window the tail read returns.
 *
 * Returns `null` for a move that cannot be expressed (an arbitrary jump, or a
 * step whose cursor is missing because the current page came back empty). The
 * caller must then leave the paginator on its current page: advancing the page
 * index without fetching the matching rows is what desynchronises the two.
 */
export const pageCursorArgs = ({
  requestedPage,
  currentPage,
  pageCount,
  pageSize,
  startCursor,
  endCursor,
}: {
  requestedPage: number;
  currentPage: number;
  pageCount: number;
  pageSize: number;
  startCursor?: string | null;
  endCursor?: string | null;
}): PageCursorArgs | null => {
  if (requestedPage === currentPage) {
    return null;
  }
  if (requestedPage < 0 || (pageCount > 0 && requestedPage > pageCount - 1)) {
    return null;
  }

  // Head of the list: reachable with no cursor at all.
  if (requestedPage === 0) {
    return { first: pageSize };
  }

  if (requestedPage === currentPage + 1) {
    return isNil(endCursor) ? null : { first: pageSize, after: endCursor };
  }

  if (requestedPage === currentPage - 1) {
    return isNil(startCursor) ? null : { last: pageSize, before: startCursor };
  }

  // Tail of the list: a backwards take with no cursor reads the final rows.
  if (requestedPage === pageCount - 1) {
    return { last: pageSize };
  }

  return null;
};
